// lib/securityUtils.js
// Security utilities for token validation and protection

// Decode JWT token (without verification - for client-side info only)
export const decodeToken = (token) => {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
};

// Check if token is expired
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    const decoded = decodeToken(token);
    if (!decoded || !decoded.exp) return true;

    const currentTime = Date.now() / 1000;
    return decoded.exp < currentTime;
  } catch (error) {
    return true;
  }
};

// Validate token format
export const isValidTokenFormat = (token) => {
  if (!token || typeof token !== "string") return false;

  // JWT should have 3 parts separated by dots
  const parts = token.split(".");
  return parts.length === 3;
};

// Check authentication status
export const checkAuth = () => {
  const token = localStorage.getItem("authToken");
  const isLoggedIn = localStorage.getItem("isLoggedIn") === "true";

  if (!isLoggedIn || !token) {
    return { isAuthenticated: false, reason: "No credentials" };
  }

  if (!isValidTokenFormat(token)) {
    return { isAuthenticated: false, reason: "Invalid token format" };
  }

  if (isTokenExpired(token)) {
    return { isAuthenticated: false, reason: "Token expired" };
  }

  return { isAuthenticated: true };
};

// Clear all authentication data
export const clearAuthData = () => {
  localStorage.removeItem("isLoggedIn");
  localStorage.removeItem("userId");
  localStorage.removeItem("roleName");
  localStorage.removeItem("userName");
  localStorage.removeItem("authToken");
  localStorage.removeItem("redirectAfterLogin");
};

// Sanitize input to prevent XSS attacks
export const sanitizeInput = (input) => {
  if (typeof input !== "string") return input;

  const map = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#x27;",
    "/": "&#x2F;",
  };
  const reg = /[&<>"'/]/gi;
  return input.replace(reg, (match) => map[match]);
};

// Validate user input before sending to API
export const validateLoginInput = (username, password) => {
  const errors = {};

  if (!username || username.trim() === "") {
    errors.username = "Username is required";
  }

  if (!password || password.trim() === "") {
    errors.password = "Password is required";
  }

  // Check for suspicious patterns
  const suspiciousPatterns = /<script|javascript:|on\w+=/i;
  if (suspiciousPatterns.test(username)) {
    errors.username = "Invalid username format";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Setup security headers for fetch requests
export const getSecureHeaders = () => {
  const token = localStorage.getItem("authToken");

  return {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};
